import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/AdminTopProductsMonthlyChart.css'; // Reutilizamos los estilos existentes

// Registrar los componentes necesarios para la gráfica de pie
Chart.register(
  ArcElement,
  Tooltip,
  Legend
);

const AdminSalesDistributionChart = ({ selectedPOS }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSales, setTotalSales] = useState(0);
  const productLimit = 10; // Límite fijo de productos a mostrar

  useEffect(() => {
    const fetchSalesDistributionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Construir la URL con los parámetros
        let url = `/api/admin-stats/sales-distribution?limit=${productLimit}`;
        if (selectedPOS && selectedPOS !== 'all') {
          url += `&pointOfSaleId=${selectedPOS}`;
        }
        
        const response = await axios.get(url);
        
        if (response.data && response.data.success) {
          const products = response.data.data;
          const totalSalesAmount = parseFloat(response.data.totalSales || 0);
          
          setTotalSales(totalSalesAmount);
          
          if (products.length === 0) {
            setChartData({
              labels: [],
              datasets: []
            });
            return;
          }
          
          // Limitar a los primeros N productos para mejor visualización
          const topProducts = products.slice(0, productLimit);
          
          // Si hay más productos que el límite, agrupar el resto como "Otros"
          let otherProductsAmount = 0;
          let otherProductsPercentage = 0;
          
          if (products.length > productLimit) {
            for (let i = productLimit; i < products.length; i++) {
              otherProductsAmount += parseFloat(products[i].total_amount);
              otherProductsPercentage += parseFloat(products[i].percentage);
            }
          }
          
          // Preparar los datos para la gráfica
          const labels = topProducts.map(product => {
            // Truncar nombres largos para mejor visualización
            const name = product.prod_name;
            return name.length > 10 ? name.substring(0, 10) + '...' : name;
          });
          
          // Guardar nombres completos para el tooltip
          const fullNames = topProducts.map(product => product.prod_name);
          
          if (products.length > productLimit) {
            labels.push('Otros');
            fullNames.push('Otros productos');
          }
          
          const amounts = topProducts.map(product => parseFloat(product.total_amount));
          if (products.length > productLimit) {
            amounts.push(otherProductsAmount);
          }
          
          const percentages = topProducts.map(product => parseFloat(product.percentage));
          if (products.length > productLimit) {
            percentages.push(otherProductsPercentage);
          }
          
          // Generar colores dinámicamente
          const backgroundColors = generateColors(labels.length);
          
          setChartData({
            labels,
            datasets: [
              {
                label: 'Monto de Ventas',
                data: amounts,
                backgroundColor: backgroundColors,
                borderColor: backgroundColors.map(color => color.replace('0.7', '1')),
                borderWidth: 1,
                percentages: percentages, // Guardamos los porcentajes para usarlos en el tooltip
                fullNames: fullNames // Guardamos los nombres completos para el tooltip
              }
            ]
          });
        } else {
          setError('No se pudieron cargar los datos de distribución de ventas');
        }
      } catch (err) {
        console.error('Error al obtener la distribución de ventas:', err);
        // Si es un error 404 o el usuario no tiene negocio registrado, mostrar mensaje apropiado
        if (err.response?.status === 404 || err.response?.status === 500) {
          setChartData({
            labels: [],
            datasets: []
          });
          setError(null);
        } else {
          setError('Error al cargar los datos');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSalesDistributionData();
  }, [selectedPOS, productLimit]);
  
  // Función para generar colores dinámicamente
  const generateColors = (count) => {
    const baseColors = [
      'rgba(255, 99, 132, 0.7)',   // Rojo
      'rgba(54, 162, 235, 0.7)',   // Azul
      'rgba(255, 206, 86, 0.7)',   // Amarillo
      'rgba(75, 192, 192, 0.7)',   // Verde azulado
      'rgba(153, 102, 255, 0.7)',  // Púrpura
      'rgba(255, 159, 64, 0.7)',   // Naranja
      'rgba(199, 199, 199, 0.7)',  // Gris
      'rgba(83, 102, 255, 0.7)',   // Azul violeta
      'rgba(255, 99, 255, 0.7)',   // Rosa
      'rgba(0, 162, 132, 0.7)',    // Verde
    ];
    
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    
    return colors;
  };
  
  // Límite fijo de productos, no necesitamos un manejador de cambios
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: -10,
        bottom: 20
      }
    },
    cutout: '60%',
    radius: '85%',
    rotation: 270, // Rotar para que comience desde arriba
    circumference: 180, // Solo mostrar 180 grados (media dona)
    
    plugins: {
      legend: {
        display: false // Ocultamos la leyenda predeterminada
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            // Mostrar el nombre completo del producto en el título
            const fullName = chartData.datasets[0].fullNames[context[0].dataIndex];
            return fullName;
          },
          label: function(context) {
            const value = context.raw ? context.raw.toLocaleString('es-MX', {
              style: 'currency',
              currency: 'MXN',
              minimumFractionDigits: 2
            }) : '';
            
            // Obtener el porcentaje correspondiente
            const percentage = chartData.datasets[0].percentages[context.dataIndex];
            
            // Solo mostrar el monto y porcentaje (sin repetir el nombre)
            return `${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="chart-wrapper">
      <div className="chart-header">
        <h3>Productos o Servicios más Vendidos</h3>
      </div>
      <div className="chart-container">
        {loading ? (
          <div className="loading-container">Cargando datos...</div>
        ) : error ? (
          <div className="error-container">{error}</div>
        ) : chartData.labels.length === 0 ? (
          <div className="no-data-container">Aún no hay datos registrados</div>
        ) : (
          <>
            <div className="pie-chart-container">
              <div className="pie-chart-wrapper">
                <Doughnut data={chartData} options={options} />
              </div>
            </div>
            
            <div className="custom-legend">
              <div className="legend-header">
                <div className="legend-item-product">Producto</div>
                <div className="legend-item-amount">Monto</div>
                <div className="legend-item-percentage">%</div>
              </div>
              <div className="legend-body">
                {chartData.labels.map((label, index) => (
                  <div key={index} className="legend-row">
                    <div className="legend-color" style={{ backgroundColor: chartData.datasets[0].backgroundColor[index] }}></div>
                    <div className="legend-item-product">{label}</div>
                    <div className="legend-item-amount">
                      {chartData.datasets[0].data[index].toLocaleString('es-MX', {
                        style: 'currency',
                        currency: 'MXN',
                        minimumFractionDigits: 2
                      })}
                    </div>
                    <div className="legend-item-percentage">{chartData.datasets[0].percentages[index]}%</div>
                  </div>
                ))}
              </div>
              <div className="legend-footer">
                <div className="legend-total">Total:</div>
                <div className="legend-total-amount">
                  {totalSales.toLocaleString('es-MX', {
                    style: 'currency',
                    currency: 'MXN',
                    minimumFractionDigits: 2
                  })}
                </div>
                <div className="legend-total-percentage">100%</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSalesDistributionChart;
